/**
 * Turns rolled conditions into colour-grading numbers. Kept here rather than in the scene so the
 * curve that decides what dusk looks like is unit-tested and shared by anything that draws the
 * world — the play camera today, a minimap or a preview thumbnail later.
 */

import { clamp } from '../math.ts';
import type { RolledEnvironment } from './roll.ts';

export type Grade = {
  /** Added to every channel; negative darkens. Suits `ColorMatrix.brightness`. */
  brightness: number;
  /** Saturation offset; negative drains colour. Suits `ColorMatrix.saturate`. */
  saturate: number;
  /** How strongly to pull the image towards `tint`, 0 to 1. */
  tintAmount: number;
  /** The colour night grades towards. */
  tint: number;
};

/** The blue a fully dark scene settles on, rather than a flat black that hides the water. */
export const NIGHT_TINT = 0x1b3a63;

/**
 * Daylight as a function of `timeOfDay`. Noon (0.5) is full daylight and midnight (0 or 1) is
 * fully dark, with a cosine between them so dawn and dusk arrive gradually instead of as a step.
 */
export function daylight(timeOfDay: number): number {
  const t = clamp(timeOfDay, 0, 1);
  return (1 - Math.cos(t * Math.PI * 2)) / 2;
}

/**
 * Night has to stay playable: the boat, its movement circle and the next gate all have to be
 * findable. The tint carries most of the mood, so the brightness cut stays modest.
 */
const MAX_NIGHT_DIMMING = 0.3;
const MAX_NIGHT_DESATURATION = 0.6;

export function gradeFor(rolled: RolledEnvironment): Grade {
  const light = daylight(rolled.timeOfDay);
  const darkness = 1 - light;

  return {
    brightness: -(darkness * MAX_NIGHT_DIMMING),
    saturate: -(darkness * MAX_NIGHT_DESATURATION),
    tintAmount: darkness,
    tint: NIGHT_TINT,
  };
}

/** True when the grade would not change a single pixel, so the filter can be skipped entirely. */
export function isNeutralGrade(grade: Grade): boolean {
  return grade.brightness === 0 && grade.saturate === 0 && grade.tintAmount === 0;
}
